import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Modal,
  Box,
  Typography,
  Button,
  TextField,
  Alert,
  Chip,
  Divider,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
} from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ModalBox from "../../themes/overrides/ModalBox";
import LoadingOverlay from "../../components/LoadingOverlay";
import { aiMatchContentModel } from "../../api/api";

const MAX_IMAGE_BYTES = 8 * 1024 * 1024; // 8MB — keep request well under the 50mb body cap

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

/**
 * AI content-model finder. The user pastes sample data and/or uploads a section
 * screenshot; the backend recommends an existing model or proposes a new one.
 * Nothing is created here — choosing "Create this model" hands the proposal back
 * to the parent, which opens the normal create flow pre-filled for review.
 */
const AiContentModelFinder = ({ isOpen, onClose, onProposeNew }) => {
  const navigate = useNavigate();
  const { workspaceId } = useParams();

  const [sampleData, setSampleData] = useState("");
  const [image, setImage] = useState(null); // data URL
  const [imageName, setImageName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const reset = () => {
    setSampleData("");
    setImage(null);
    setImageName("");
    setError("");
    setResult(null);
    setLoading(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleImage = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Please choose an image file.");
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setError("Image is too large (max 8MB).");
      return;
    }
    setError("");
    setImageName(file.name);
    setImage(await fileToDataUrl(file));
  };

  const handleAnalyze = async () => {
    if (!sampleData.trim() && !image) {
      setError("Add sample data or upload a screenshot first.");
      return;
    }
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const res = await aiMatchContentModel(workspaceId, {
        sampleData: sampleData.trim() || undefined,
        image: image || undefined,
      });
      const data = res?.data;
      if (data?.status === "success") {
        setResult(data);
      } else {
        setError(data?.message || "Could not analyze the section. Try again.");
      }
    } catch (e) {
      setError(
        e?.response?.data?.message ||
          "Request failed. Check the workspace AI model configuration."
      );
    } finally {
      setLoading(false);
    }
  };

  const openExisting = () => {
    if (result?.match?.contentTypeId) {
      navigate(`/${workspaceId}/content-type/${result.match.contentTypeId}`);
      handleClose();
    }
  };

  const createFromProposal = () => {
    if (result?.proposal) {
      onProposeNew(result.proposal);
      handleClose();
    }
  };

  const match = result?.match;
  const proposal = result?.proposal;

  return (
    <>
      <Modal open={isOpen} onClose={handleClose}>
        <ModalBox
          sx={{
            width: { xs: "95%", sm: "640px", md: "760px" },
            maxHeight: { xs: "95vh", sm: "90vh" },
            overflowY: "auto",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
            <Typography variant="h5">Find a Content Model with AI</Typography>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Paste sample data for a section and/or upload a screenshot. AI will
            check whether an existing model fits, or propose a new one.
          </Typography>

          <Alert severity="warning" sx={{ mb: 2 }}>
            Don&apos;t upload screenshots or data containing real customer
            information — the content is sent to the configured AI provider.
          </Alert>

          {!result && (
            <>
              <TextField
                label="Sample data (JSON, text, or HTML)"
                multiline
                minRows={5}
                fullWidth
                value={sampleData}
                onChange={(e) => setSampleData(e.target.value)}
                placeholder='e.g. { "heading": "Summer Sale", "cta": "Book now", "image": "banner.png" }'
                sx={{ mb: 2 }}
              />

              <Box
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  handleImage(e.dataTransfer.files?.[0]);
                }}
                sx={{
                  border: "1px dashed",
                  borderColor: "divider",
                  borderRadius: 1,
                  p: 2,
                  textAlign: "center",
                  mb: 2,
                }}
              >
                <Button component="label" startIcon={<CloudUploadOutlinedIcon />}>
                  Upload section screenshot
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(e) => handleImage(e.target.files?.[0])}
                  />
                </Button>
                {imageName && (
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    {imageName}{" "}
                    <Button size="small" onClick={() => { setImage(null); setImageName(""); }}>
                      remove
                    </Button>
                  </Typography>
                )}
              </Box>
            </>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {result && (
            <Box sx={{ mb: 2 }}>
              {match?.contentTypeId ? (
                <Box
                  sx={{
                    mb: 2,
                    p: 2,
                    borderRadius: 1,
                    border: "2px solid",
                    borderColor: match.found ? "success.main" : "warning.main",
                    bgcolor: match.found ? "rgba(46,125,50,0.06)" : "rgba(237,108,2,0.08)",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
                    <Typography variant="h6">
                      {match.found ? "Existing model fits" : "Closest existing model"}: {match.name}
                    </Typography>
                    <Chip
                      size="small"
                      color={match.confidence >= 70 ? "success" : "warning"}
                      label={`${match.confidence}% match`}
                    />
                  </Box>
                  {!match.found && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Not a perfect fit, but the nearest reusable model. Review the mapping
                      below — reuse it, or create the suggested new model instead.
                    </Typography>
                  )}
                  {Array.isArray(match.fieldMapping) && match.fieldMapping.length > 0 && (
                    <Table size="small" sx={{ mb: 1 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell>Section field</TableCell>
                          <TableCell>Model field</TableCell>
                          <TableCell>Note</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {match.fieldMapping.map((m, i) => (
                          <TableRow key={i}>
                            <TableCell>{m.sectionField}</TableCell>
                            <TableCell>{m.modelField}</TableCell>
                            <TableCell>{m.note}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                  <Button variant="contained" onClick={openExisting}>
                    Open {match.name}
                  </Button>
                </Box>
              ) : (
                <Alert severity="info" sx={{ mb: 2 }}>
                  No existing models to compare against. Here&apos;s a suggested new model.
                </Alert>
              )}

              {result.reasoning && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {result.reasoning}
                </Typography>
              )}

              <Divider sx={{ mb: 2 }} />

              {proposal && (
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    {match?.contentTypeId ? "Or create a new model" : "Suggested model"}: {proposal.name}
                  </Typography>
                  {proposal.fields?.length > 0 && (
                    <Table size="small" sx={{ mb: 1 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell>Field</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Required</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {proposal.fields.map((f, i) => (
                          <TableRow key={i}>
                            <TableCell>{f.name}</TableCell>
                            <TableCell>
                              {f.type}
                              {f.array_type ? ` (${f.array_type})` : ""}
                            </TableCell>
                            <TableCell>{f.required ? "Yes" : "No"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                  <Button variant="outlined" onClick={createFromProposal} disabled={!proposal.name}>
                    Create this model
                  </Button>
                </Box>
              )}
            </Box>
          )}

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 1 }}>
            {result ? (
              <Button onClick={reset}>Start over</Button>
            ) : (
              <Button variant="contained" onClick={handleAnalyze} disabled={loading}>
                {loading ? "Analyzing..." : "Analyze"}
              </Button>
            )}
          </Box>

          {loading && <LoadingOverlay />}
        </ModalBox>
      </Modal>
    </>
  );
};

export default AiContentModelFinder;
